/**
 * QuizList (Sprint 6 B4) — list quiz của 1 course.
 *
 * Route: /courses/:slug/quizzes
 * UX:
 *   - Breadcrumb: Home → Môn học → {course} → Bài kiểm tra
 *   - Card grid với title + duration + threshold + max_attempts + webcam tag
 *   - Click "Bắt đầu thi" → navigate /quizzes/:id/consent (B6 — Sprint 6 placeholder)
 *   - Chưa enroll → empty state
 */

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  HomeOutlined,
  RightOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCourseDetail } from "@/api/catalog";
import { useEnrollmentFor } from "@/api/enrollment";
import { type QuizListItem, formatQuizTime, quizWindowState, useQuizzes } from "@/api/assessment";
import { useAuthStore } from "@/stores/auth";

const { Title, Text, Paragraph } = Typography;

const NO_TERM_KEY = "no-term";

interface TermGroup {
  key: string;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
  quizzes: QuizListItem[];
}

/** Nhóm bài theo kỳ học: kỳ mới nhất trước, bài tập dùng chung mọi kỳ (không
 * gắn kỳ) ở nhóm cuối. Trong mỗi kỳ: bài có giờ mở sớm hơn đứng trước. */
function groupByTerm(quizzes: QuizListItem[]): TermGroup[] {
  const map = new Map<string, TermGroup>();
  for (const q of quizzes) {
    const key = q.term_offering ? String(q.term_offering) : NO_TERM_KEY;
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: q.term_offering_name ?? "Bài tập (dùng chung mọi kỳ)",
        startsAt: q.term_offering_starts_at ?? null,
        endsAt: q.term_offering_ends_at ?? null,
        quizzes: [],
      });
    }
    map.get(key)!.quizzes.push(q);
  }
  for (const g of map.values()) {
    g.quizzes.sort((a, b) => (a.opens_at ?? "").localeCompare(b.opens_at ?? ""));
  }
  return [...map.values()].sort((a, b) => {
    if (a.key === NO_TERM_KEY) return 1;
    if (b.key === NO_TERM_KEY) return -1;
    return (b.startsAt ?? "").localeCompare(a.startsAt ?? "");
  });
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function termStatus(g: TermGroup, today: string): "current" | "past" | "future" | null {
  if (g.key === NO_TERM_KEY || !g.startsAt) return null;
  if (g.endsAt && g.endsAt < today) return "past";
  if (g.startsAt > today) return "future";
  return "current";
}

export function QuizList() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: course, isLoading: courseLoading } = useCourseDetail(slug);
  const { enrollment } = useEnrollmentFor(slug);
  const { data: quizzes, isLoading, error } = useQuizzes(slug);
  // Giảng viên/admin xem trước quiz không cần enroll (review nội dung). Họ KHÔNG
  // "Bắt đầu thi" (tránh tạo Attempt rác — BE cũng chặn) mà mở trình soạn admin.
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === "instructor" || role === "tenant_admin" || role === "superadmin";

  if (courseLoading || isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error || !course) {
    return (
      <Result
        status="404"
        title="Không tìm thấy môn học"
        extra={<Link to="/courses">← Về danh sách môn học</Link>}
      />
    );
  }

  const isEnrolled = enrollment?.status === "enrolled" || enrollment?.status === "completed";
  const canView = isEnrolled || isStaff;

  // Chia theo kỳ học (BE đã chỉ trả bài của các kỳ học viên đã đăng ký) —
  // mặc định mở kỳ hiện tại, không có thì kỳ mới nhất.
  const today = todayIso();
  const groups = groupByTerm(quizzes ?? []);
  const defaultTermKey =
    groups.find((g) => termStatus(g, today) === "current")?.key ?? groups[0]?.key;

  const renderQuizCard = (quiz: QuizListItem) => {
    const windowState = quizWindowState(quiz);
    return (
      <Col xs={24} sm={12} lg={8} key={quiz.id}>
        <Card
          hoverable
          title={quiz.title}
          actions={[
            isStaff ? (
              <Link key="preview" to={`/admin/courses/${slug}/quizzes/${quiz.id}`}>
                <Button type="default" icon={<EyeOutlined />}>
                  Xem câu hỏi
                </Button>
              </Link>
            ) : (
              <Button
                key="start"
                type="primary"
                icon={<RightOutlined />}
                disabled={windowState !== "open"}
                onClick={() => navigate(`/quizzes/${quiz.id}/consent`)}
              >
                {windowState === "not_open"
                  ? "Chưa đến giờ làm bài"
                  : windowState === "closed"
                    ? "Đã hết giờ làm bài"
                    : "Bắt đầu thi"}
              </Button>
            ),
          ]}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {quiz.description && (
              <Paragraph type="secondary" ellipsis={{ rows: 2 }}>
                {quiz.description}
              </Paragraph>
            )}
            <Space wrap>
              <Tag color="blue" icon={<ClockCircleOutlined />}>
                {quiz.duration_display}
              </Tag>
              <Tag color="green">Pass {quiz.pass_threshold_pct}%</Tag>
              {quiz.max_attempts > 0 && <Tag>Tối đa {quiz.max_attempts} lần</Tag>}
              {quiz.webcam_required && (
                <Tag color="orange" icon={<VideoCameraOutlined />}>
                  Cần webcam
                </Tag>
              )}
            </Space>
            {(quiz.opens_at || quiz.closes_at) && (
              <div data-testid={`quiz-window-${quiz.id}`}>
                <Text>
                  <CalendarOutlined /> Thời gian làm bài:{" "}
                  <strong>
                    {quiz.opens_at ? formatQuizTime(quiz.opens_at) : "…"}
                    {" → "}
                    {quiz.closes_at ? formatQuizTime(quiz.closes_at) : "…"}
                  </strong>
                </Text>{" "}
                {windowState === "open" && <Tag color="green">Đang mở</Tag>}
                {windowState === "not_open" && <Tag color="gold">Chưa mở</Tag>}
                {windowState === "closed" && <Tag color="default">Đã đóng</Tag>}
              </div>
            )}
            <Text type="secondary">
              Tổng điểm: <strong>{quiz.total_points}</strong>
            </Text>
          </Space>
        </Card>
      </Col>
    );
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: <Link to="/courses">Môn học</Link> },
          { title: <Link to={`/my-courses/${slug}`}>{course.name}</Link> },
          { title: "Bài kiểm tra" },
        ]}
      />

      <Title level={2}>
        <TrophyOutlined /> Bài kiểm tra — {course.name}
      </Title>

      {!isEnrolled && !isStaff && (
        <Alert
          type="warning"
          showIcon
          message="Bạn cần đăng ký môn học trước khi xem bài kiểm tra."
          action={
            <Link to={`/my-courses/${slug}`}>
              <Button size="small" type="primary">
                Đăng ký
              </Button>
            </Link>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {isStaff && !isEnrolled && (
        <Alert
          type="info"
          showIcon
          icon={<EyeOutlined />}
          message="Chế độ xem trước (giảng viên)"
          description="Bạn đang xem danh sách bài kiểm tra để duyệt nội dung. Mở 'Xem câu hỏi' để soạn/kiểm tra chi tiết. Không tạo lượt làm bài."
          style={{ marginBottom: 24 }}
        />
      )}

      {canView && (!quizzes || quizzes.length === 0) && (
        <Empty description="Môn học này chưa có bài kiểm tra nào." style={{ padding: 48 }} />
      )}

      {canView && quizzes && quizzes.length > 0 && (
        <Tabs
          defaultActiveKey={defaultTermKey}
          data-testid="quiz-term-tabs"
          items={groups.map((g) => {
            const st = termStatus(g, today);
            return {
              key: g.key,
              label: (
                <span>
                  {g.name} {st === "current" && <Tag color="blue">Kỳ hiện tại</Tag>}
                  {st === "past" && <Tag>Đã kết thúc</Tag>}
                  {st === "future" && <Tag color="gold">Sắp tới</Tag>}
                </span>
              ),
              children: (
                <Row gutter={[16, 16]} data-testid="quiz-grid">
                  {g.quizzes.map(renderQuizCard)}
                </Row>
              ),
            };
          })}
        />
      )}
    </div>
  );
}

export default QuizList;
